<template>
    <li class="treeselect-option-node">
        <div
            class="treeselect-option-node__label-wrapper"
            :class="{
                'treeselect-option-node--branch': isBranch,
                'treeselect-option-node--leaf': !isBranch,
                'treeselect-option-node--disabled': disabled,
                'treeselect-option-node--no-checkbox': hideBranchCheckboxes && isBranch,
                'treeselect-option-node--highlighted': isHighlighted
            }"
            @mouseenter="handleMouseEnter">
            <!-- Expand/collapse arrow (branch only) -->
            <span v-if="isBranch" class="treeselect-arrow" @click.stop="handleToggleExpand">
                <i class="fa-solid fa-caret-right" :class="{ 'fa-rotate-90 rotated' : isExpanded }"></i>
                <!--                <i v-else class="fa-solid fa-caret-right"></i>-->
            </span>
            <span v-else-if="!hideBranchCheckboxes" class="treeselect-arrow-placeholder"/>

            <!-- Checkbox (for multi) or a placeholder if disabled -->
            <b-checkbox v-if="!hideBranchCheckboxes || !isBranch" class="treeselect-checkbox" type="checkbox" :disabled="disabled" :checked="isChecked" :indeterminate="isIndeterminate" @change="handleToggleCheck">
                <span class="treeselect-option-node__label">
                    <slot name="option-label" :node="node" :count="descendantCount" :shouldShowCount="shouldShowCount">
                      <div class="d-flex align-items-center">
                          <div>{{ node[label] }}</div>
                        <span v-if="shouldShowCount && isBranch">({{ descendantCount }})</span>
                      </div>
                    </slot>
                </span>
            </b-checkbox>
            <label v-else class="treeselect-option-node__label" @click.stop="handleToggleExpand">
                <slot name="option-label" :node="node" :count="descendantCount" :shouldShowCount="shouldShowCount">
                  <div class="d-flex align-items-center">
                      <div>{{ node[label] }}</div>
                    <span v-if="shouldShowCount && isBranch">({{ descendantCount }})</span>
                  </div>
                </slot>
            </label>
        </div>

        <!-- Children list -->
        <ul v-if="isBranch && isExpanded && hasLoadedChildren" class="treeselect-children-list">
            <e-treeselect-option
                v-for="child in node.children"
                :key="child[trackBy]"
                :track-by="trackBy"
                :node="child"
                :level="level + 1"
                :multiple="multiple"
                :flat="flat"
                :disable-branch-nodes="disableBranchNodes"
                :hide-branch-checkboxes="hideBranchCheckboxes"
                :value-consists-of="valueConsistsOf"
                :checked-ids="checkedIds"
                :indeterminate-ids="indeterminateIds"
                :expanded-ids="expandedIds"
                :label="label"
                :onLoadChildren="onLoadChildren"
                :search-query="searchQuery"
                :search-nested="searchNested"
                :disable-fuzzy-matching="disableFuzzyMatching"
                :show-count="showCount"
                :instance-id="instanceId"
                :highlighted-node-id="highlightedNodeId"
                :emitEvent="emitEvent"
                :onCheck="onCheck"
                :onExpand="onExpand"
                :default-expand-level="defaultExpandLevel"
            >
                <template v-slot:option-label="slotProps">
                    <slot name="option-label" v-bind="slotProps" />
                </template>
            </e-treeselect-option>
        </ul>

        <!-- If branch is expanded but children=null => trigger load -->
        <div v-else-if="isBranch && isExpanded && node.children === null" class="treeselect-loader">
            <slot name="loading">Loading...</slot>
        </div>
    </li>
</template>

<script>
export default {
    name: `e-treeselect-option`,
    props: {
        node: { type: Object, required: true },
        trackBy: { type: String, default: `id` },
        level: { type: Number, default: 0 },
        multiple: { type: Boolean, default: false },
        flat: { type: Boolean, default: false },
        disableBranchNodes: { type: Boolean, default: false },
        hideBranchCheckboxes: { type: Boolean, default: false },
        valueConsistsOf: { type: String, default: `BRANCH_PRIORITY` },
        checkedIds: { type: Array, default: () => [] },
        indeterminateIds: { type: Array, default: () => [] },
        expandedIds: { type: Array, default: () => [] },
        label: { type: String, default: `label` },
        searchQuery: { type: String, default: `` },
        searchNested: { type: Boolean, default: false },
        disableFuzzyMatching: { type: Boolean, default: false },
        showCount: { type: Boolean, default: false },
        instanceId: { type: [String, Number], default: null },
        highlightedNodeId: { type: [String, Number, null], default: null },
        onLoadChildren: { type: Function, required: false },
        // These are callbacks passed down from parent
        emitEvent: { type: Function, required: true },
        onCheck: { type: Function, required: true },
        onExpand: { type: Function, required: true },

        defaultExpandLevel: { type: Number, default: 0 }
    },
    computed: {
        isBranch() {
            // It's a branch if children is an array (non-empty) or null (unloaded).
            return (Array.isArray(this.node.children) && this.node.children.length > 0) || this.node.children === null;
        },
        disabled() {
            // If node is explicitly disabled, or if it's a branch and disableBranchNodes
            return this.node.isDisabled || (this.isBranch && (this.disableBranchNodes));
        },
        isExpanded() {
            return this.expandedIds.includes(this.node[this.trackBy]);
        },
        hasLoadedChildren() {
            // children is an array => loaded
            return Array.isArray(this.node.children) && this.node.children.length > 0;
        },
        isChecked() {
            return this.checkedIds.includes(this.node[this.trackBy]) || this.indeterminateIds.includes(this.node[this.trackBy]);
        },
        isIndeterminate() {
            return this.indeterminateIds.includes(this.node[this.trackBy]);
        },
        isHighlighted() {
            return this.highlightedNodeId === this.node[this.trackBy];
        },
        descendantCount() {
            // Just a simple count of immediate children for demonstration
            if (!this.isBranch || !Array.isArray(this.node.children)) return 0;
            return this.node.children.length;
        },
        shouldShowCount() {
            return this.showCount && this.isBranch;
        }
    },
    methods: {
        handleMouseEnter() {
            // Could trigger highlight logic in the parent if needed
        },
        handleToggleExpand() {
            if (!this.isBranch) return;
            this.onExpand(this.node[this.trackBy], !this.isExpanded);
        },
        handleToggleCheck() {
            if (this.disabled) return;
            this.onCheck(this.node);
        }
    },
    watch: {
      isExpanded(newVal) {
        if (newVal && this.node.children === null && typeof this.onLoadChildren === `function`) {
          this.onLoadChildren(this.node);
        }
      }
    }
};
</script>

<style scoped>
.treeselect-option-node__label-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    line-height: 1.6;
    text-wrap: nowrap;
}
.treeselect-option-node__label-wrapper:hover {
    background: rgba(0, 0, 0, 0.05);
}

.treeselect-arrow {
    min-width: 1.2em;
    text-align: center;
    cursor: pointer;
    margin-right: 0.2em;

    i {
        position: relative;
        transition: transform .2s cubic-bezier(.19,1,.22,1);
    }

    .rotated {
        top: -1px;
    }
}
.treeselect-arrow-placeholder {
    min-width: 1.2em;
    margin-right: 0.2em;
}

.treeselect-checkbox {
    display: flex;
    width: 100%;
    /*margin-right: 6px;*/

    input, label {
        cursor: pointer;
    }

  .custom-control-label {
    width: 100%;
      flex: 1;
  }

  .custom-control-label::after, .custom-control-label::before {
    top: 50%;
    transform: translateY(-50%);
  }
}
.treeselect-placeholder-check {
    min-width: 1.2em;
    margin-right: 6px;
}

.treeselect-option-node__label {
    flex: 1;
}

/* States */
.treeselect-option-node--disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.treeselect-option-node--no-checkbox {
    .treeselect-checkbox {
        padding-left: 1rem;
    }
}
.treeselect-option-node--highlighted {
    background: #f0f0f0;
}

/* Children list indentation */
.treeselect-children-list {
    position: relative;
    list-style-type: none;
    padding: 0;
    margin-left: 20px;

    &:after {
        border-left: 1px dotted #ccc;
        content: '';
        display: block;
        position: absolute;
        width: 1px;
        inset: 0;
        left: -0.7em;
    }
}

/* Loading placeholder if children=null */
.treeselect-loader {
    margin-left: 2em;
    font-size: 0.9em;
    color: #999;
}
</style>
